/**
 * @file site-path-rules.ts
 * @description Правила адресов витрины: общий сопоставитель путей и списки
 * адресов, где не работают счётчики и Вебвизор (phase19.16, партия Р3г,
 * v0.19.62; site-builder.md SB136, SB133; db-design-r3g §6.2, §6.3;
 * integrations-r3g-counters §1 «Дополнение к §6.3»; поправка оркестратора
 * 2026-10-23 — полная загрузка страницы при переходе во ВСЕ исключённые
 * адреса, не только в /checkout).
 *
 * Зачем один сопоставитель: «Сайт закрыт» (SB138, site-closure.ts) и
 * согласие на cookie решают одну задачу — «попадает ли адрес под правило» —
 * и должны понимать `/lk` одинаково (`/lk/orders` да, `/lkx` нет). До Р3г
 * сопоставитель жил в site-closure.ts; там остались прежние имена-обёртки.
 *
 * Почему ЛК и подтверждение заказа исключены целиком (решение §7.29 п. 5):
 * ссылки из писем несут секретные ключи доступа (`/lk/setup?token`,
 * `/lk/reset`, `/lk/reviews?token`, `/lk/orders/:id/approve?token`,
 * `/order-confirm/<token>`). Очистка адреса закрывает только ручные `hit`,
 * а карта кликов, внешние ссылки и Вебвизор шлют настоящий `location.href`
 * (integrations-r3g-counters §1) — поэтому счётчик там не грузится вовсе.
 *
 * Модуль без зависимостей от Node и браузера (`URL` есть и там, и там).
 */

import { deepFreeze } from "../site-theme/deep-freeze.js";

// ── Общий сопоставитель ──────────────────────────────────────────────────

/**
 * Как сравнивать адрес с правилом:
 *   - `segment` — сам адрес и всё под ним по сегментам: `/lk` → `/lk`, `/lk/orders`, но не `/lkx`;
 *   - `exact`   — только этот адрес (`/robots.txt`);
 *   - `stem`    — файл-иконка Next с номером и расширением или каталогом:
 *                 `/icon` → `/icon`, `/icon.png`, `/icon1.png`, `/icon/small`, но не `/iconography`.
 */
export type SitePathMatch = "segment" | "exact" | "stem";

export interface SitePathRule {
  readonly path: string;
  readonly match: SitePathMatch;
}

/**
 * Приводит адрес к виду для сравнения: без query/hash, с ведущим слэшем,
 * без повторов и завершающего слэша. Регистр не меняем: `/LK` — другой адрес.
 */
export function normalizeSitePath(pathname: string): string {
  const cut = pathname.split(/[?#]/u, 1)[0] ?? "";
  const collapsed = `/${cut}`.replace(/\/{2,}/gu, "/");
  return collapsed.length > 1 && collapsed.endsWith("/") ? collapsed.slice(0, -1) : collapsed;
}

/** Хвост после основы иконки: пусто, номер, `.расширение` или подкаталог. */
const ICON_STEM_TAIL = /^\d*(?:\.[A-Za-z0-9]+|\/.*)?$/u;

/** Попадает ли уже нормализованный адрес под правило. */
export function matchesSitePathRule(path: string, rule: SitePathRule): boolean {
  switch (rule.match) {
    case "exact":
      return path === rule.path;
    case "segment":
      return path === rule.path || path.startsWith(`${rule.path}/`);
    case "stem":
      return path.startsWith(rule.path) && ICON_STEM_TAIL.test(path.slice(rule.path.length));
  }
}

/** Попадает ли адрес (любой формы: с query, хвостовым слэшем) под одно из правил. */
export function matchesSitePathRules(pathname: string, rules: readonly SitePathRule[]): boolean {
  const path = normalizeSitePath(pathname);
  return rules.some((rule) => matchesSitePathRule(path, rule));
}

// ── Согласие на cookie и счётчики (SB136, db-design-r3g §6.3) ─────────────

const seg = (path: string): SitePathRule => ({ path, match: "segment" });

/** Служебные адреса: ни баннера, ни счётчиков (предпросмотр, полоса сотрудника, заглушка SB138). */
export const CONSENT_HIDDEN_PATHS: readonly SitePathRule[] = deepFreeze<SitePathRule[]>([
  seg("/preview"),
  seg("/site-preview"),
  seg("/site-staff"),
  seg("/site-closed"),
  seg("/healthz"),
]);

/**
 * Где счётчики не грузятся: служебные + весь ЛК и подтверждение заказа
 * (секретные ключи из писем в адресах, ПДн). Баннер на `/lk` и
 * `/order-confirm` остаётся — компактный.
 */
export const COUNTERS_EXCLUDED_PATHS: readonly SitePathRule[] = deepFreeze<SitePathRule[]>([
  ...CONSENT_HIDDEN_PATHS,
  seg("/lk"),
  seg("/order-confirm"),
]);

/** Где Вебвизор не пишет никогда: оформление заказа + все адреса без счётчиков (SB133, SB136). */
export const WEBVISOR_EXCLUDED_PATHS: readonly SitePathRule[] = deepFreeze<SitePathRule[]>([
  ...COUNTERS_EXCLUDED_PATHS,
  seg("/checkout"),
]);

/** Где баннер — компактная полоса в 2 строки (SB134). */
export const CONSENT_COMPACT_PATHS: readonly SitePathRule[] = deepFreeze<SitePathRule[]>([
  seg("/checkout"),
  seg("/order-confirm"),
  seg("/lk"),
]);

/** Баннер не показывается вовсе (предпросмотр, заглушка, служебные). */
export function isConsentBannerHiddenPath(pathname: string): boolean {
  return matchesSitePathRules(pathname, CONSENT_HIDDEN_PATHS);
}

/** Можно ли грузить счётчики на этом адресе (без учёта согласия, предпросмотра, канвы и пропуска сотрудника). */
export function isCountersAllowedPath(pathname: string): boolean {
  return !matchesSitePathRules(pathname, COUNTERS_EXCLUDED_PATHS);
}

/** Можно ли включать Вебвизор на этом адресе. */
export function isWebvisorAllowedPath(pathname: string): boolean {
  return !matchesSitePathRules(pathname, WEBVISOR_EXCLUDED_PATHS);
}

/** Компактная полоса баннера на этом адресе. */
export function isCompactConsentPath(pathname: string): boolean {
  return matchesSitePathRules(pathname, CONSENT_COMPACT_PATHS);
}

/** Путь из адреса любой формы (`/a?b`, `https://host/a`, `a`): только pathname. */
function pathOf(href: string): string {
  try {
    return new URL(href, "http://site.invalid").pathname;
  } catch {
    return normalizeSitePath(href);
  }
}

export interface FullPageLoadOptions {
  /** Скрипты счётчиков на текущей странице загружены (по умолчанию — да, осторожно). */
  countersLoaded?: boolean;
  /** Вебвизор на текущей странице пишет (по умолчанию — да, осторожно). */
  webvisor?: boolean;
}

/**
 * Нужна ли полная загрузка страницы вместо перехода без перезагрузки.
 * Скрипт счётчика нельзя выгрузить на ходу: после перехода без перезагрузки
 * карта кликов, Вебвизор и `code.js` VK продолжили бы работу на исключённой
 * странице. Поэтому:
 *   - со страницы со счётчиками в адрес без счётчиков (ЛК, подтверждение
 *     заказа, служебные) — полная загрузка;
 *   - при пишущем Вебвизоре со страницы, где он пишет, в адрес без Вебвизора
 *     (`/checkout`) — полная загрузка (там Метрика стартует с `webvisor: false`).
 * Обратные переходы (из ЛК в каталог) — без перезагрузки: загрузчик сам
 * запустит счётчики на разрешённой странице.
 */
export function needsFullPageLoad(from: string, to: string, opts: FullPageLoadOptions = {}): boolean {
  if (opts.countersLoaded === false) return false;
  const [a, b] = [pathOf(from), pathOf(to)];
  if (isCountersAllowedPath(a) && !isCountersAllowedPath(b)) return true;
  const webvisor = opts.webvisor ?? true;
  return webvisor && isWebvisorAllowedPath(a) && !isWebvisorAllowedPath(b);
}

// ── Очистка адреса для отправки посещения (SB136) ────────────────────────

/** Параметры, которые остаются в адресе посещения: метки рекламы. */
function keepCounterParam(name: string): boolean {
  return name.startsWith("utm_") || name === "yclid";
}

/**
 * Адрес для `ym(id, "hit", …)` и `_tmr.push({ url, referrer })`: origin +
 * путь + только `utm_*` и `yclid`, без `#` (там бывают ключи) и без любых
 * других параметров (`?token=` и прочее). Относительный адрес без `base` —
 * путь с отфильтрованными параметрами. Нечитаемый адрес — пустая строка
 * (вызывающий не отправляет посещение).
 */
export function sanitizeCounterUrl(href: string, base?: string): string {
  let url: URL;
  const relative = base === undefined && !/^[a-z][a-z0-9+.-]*:/iu.test(href);
  try {
    url = new URL(href, base ?? "http://site.invalid");
  } catch {
    return "";
  }
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, name) => {
    if (keepCounterParam(name)) kept.append(name, value);
  });
  const query = kept.toString();
  const path = `${url.pathname}${query === "" ? "" : `?${query}`}`;
  return relative ? path : `${url.origin}${path}`;
}
